import type { EdgeSubscriptionData, EdgeSubscriptionFormData } from './types';
import type { CompanyUser } from './RecipientPicker';

/** Extrae array de la respuesta del API Teammates */
const extractTeammatesList = (data: unknown): unknown[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const arr = obj.data ?? obj.items ?? obj.teammates ?? obj.value ?? obj.result;
    return Array.isArray(arr) ? arr : [];
  }
  return [];
};

/** Mapea item del API Teammates a CompanyUser */
const mapTeammateItemToCompanyUser = (item: Record<string, unknown>): CompanyUser | null => {
  const email = String(item.email ?? item.emailAddress ?? item.userEmail ?? item.mail ?? '').trim();
  if (!email || !email.includes('@')) return null;
  const label =
    String(item.name ?? item.displayName ?? item.userName ?? item.fullName ?? '').trim() ||
    String(item.firstName ?? '').trim() + ' ' + String(item.lastName ?? '').trim() ||
    email.split('@')[0];
  return { email, label: label || email };
};

/** Mapea respuesta del API Teammates a CompanyUser[] */
export const mapTeammatesToCompanyUsers = (data: unknown): CompanyUser[] => {
  const list = extractTeammatesList(data);
  const result: CompanyUser[] = [];
  const seen = new Set<string>();
  for (const x of list) {
    if (x != null && typeof x === 'object') {
      const user = mapTeammateItemToCompanyUser(x as Record<string, unknown>);
      if (user && !seen.has(user.email.toLowerCase())) {
        seen.add(user.email.toLowerCase());
        result.push(user);
      }
    }
  }
  return result;
};

/** Extrae dominio de compañía desde lista de emails (más frecuente) o primer email */
export const extractCompanyDomainFromUsers = (users: { email: string }[]): string => {
  if (users.length === 0) return '';
  const domains = users
    .map(u => u.email.split('@')[1]?.toLowerCase())
    .filter(Boolean);
  if (domains.length === 0) return '';
  const counts: Record<string, number> = {};
  for (const d of domains) counts[d] = (counts[d] ?? 0) + 1;
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0] ?? domains[0] ?? '';
};

/** Mapea estados del API (PistilConnect/states) a { value, label }[] */
export const mapStatesToOptions = (data: unknown): { value: string; label: string }[] => {
  const list = Array.isArray(data) ? data : [];
  return list
    .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
    .map(x => {
      const code = String(x.stateIso ?? x.stateCode ?? x.code ?? '');
      const name = String(x.stateName ?? x.name ?? x.label ?? code);
      return { value: code, label: name };
    })
    .filter(o => o.value);
};

/** Extrae array de la respuesta del API Brands (estructura: { brandList: [...] }) */
const extractBrandsList = (data: unknown): unknown[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const arr = obj.brandList ?? obj.data ?? obj.items ?? obj.brands ?? obj.value ?? obj.result;
    return Array.isArray(arr) ? arr : [];
  }
  return [];
};

/** Mapea territorios del API /v2/Territory a { value, label }[] (value = territoryGuid) */
export const mapTerritoriesToOptions = (data: unknown): { value: string; label: string }[] => {
  const list = Array.isArray(data) ? data : [];
  return list
    .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
    .map(x => {
      const value = String(x.territoryGuid ?? x.id ?? x.guid ?? '').trim();
      const label = String(x.territoryName ?? x.name ?? x.label ?? value ?? '').trim();
      return { value, label: label || value };
    })
    .filter(o => o.value);
};

/** Mapea marcas del API Brands a { value, label, meta? }[] - deduplica por brandName (misma marca en varios estados) */
export const mapBrandsToOptions = (data: unknown): { value: string; label: string; meta?: Record<string, unknown> }[] => {
  const list = extractBrandsList(data);
  const seen = new Set<string>();
  return list
    .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
    .map(x => {
      const label = String(x.brandName ?? x.name ?? x.label ?? '').trim();
      const value = String(x.brandName ?? x.name ?? x.label ?? x.brandId ?? x.id ?? '').trim();
      const meta: Record<string, unknown> = {
        stateIso: x.stateIso,
        brandId: x.brandId,
      };
      return { value: value || label, label: label || value, meta };
    })
    .filter(o => o.value && o.label)
    .filter(o => {
      const key = o.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** Convierte startDate + startTime en expresión cron en UTC (el BE espera UTC).
 *  El sistema de envío redondea al siguiente hora completa, por lo que siempre
 *  se fuerza minuto = 0 y se redondea la hora hacia arriba si hubiera minutos. */
const buildCronFromFormData = (formData: EdgeSubscriptionFormData): string => {
  try {
    const d = formData.startDate;
    if (!d || !(d instanceof Date)) return '';
    const rawMinute = d.getUTCMinutes();
    const minute = 0; // siempre hora exacta
    const hour = rawMinute > 0 ? (d.getUTCHours() + 1) % 24 : d.getUTCHours();
    const dayOfWeek = d.getUTCDay(); // 0=Sun, 1=Mon, ..., 6=Sat (en UTC)
    if (formData.periodicity?.toLowerCase().includes('week')) {
      return `${minute} ${hour} * * ${dayOfWeek}`;
    }
    if (formData.periodicity?.toLowerCase().includes('month')) {
      const dayOfMonth = d.getUTCDate();
      return `${minute} ${hour} ${dayOfMonth} * *`;
    }
    return `${minute} ${hour} * * ${dayOfWeek}`;
  } catch {
    return '';
  }
};

/** Mapea EdgeSubscriptionFormData al payload del API PUT */
export const mapFormDataToPutPayload = (
  formData: EdgeSubscriptionFormData,
  userGuid: string
): Record<string, unknown> => {
  const recipients = formData.recipient
    ? formData.recipient.split(',').map(s => s.trim()).filter(Boolean)
    : [];
  const edgeType = formData.edgeType
    ? formData.edgeType.charAt(0).toUpperCase() + formData.edgeType.slice(1)
    : 'Brand';
  const edgePeriodicity = formData.periodicity || 'Weekly';
  const isRecurring = !formData.sendOnce;
  return {
    subscriptionName: formData.subscriptionName,
    edgeType,
    edgePeriodicity,
    brandName: formData.brandName,
    state: formData.state || '',
    cronExpression: isRecurring ? buildCronFromFormData(formData) : '',
    recipients,
    territoryGuid: formData.territory || null,
    userGuid,
  };
};

/** Parsea periodicity del BE para mostrar: Last7Days/Last14Days → Weekly, Month → Monthly */
export const formatPeriodicityFromApi = (raw: string): string => {
  if (!raw || typeof raw !== 'string') return 'Weekly';
  const lower = raw.toLowerCase().trim();
  if (lower === 'last7days' || lower === 'last14days') return 'Weekly';
  if (lower === 'month' || lower === 'monthly') return 'Monthly';
  if (lower.includes('week')) return 'Weekly';
  if (lower.includes('month')) return 'Monthly';
  return raw;
};

/** Calcula el próximo run desde expresión cron (formato: minute hour dayOfMonth month dayOfWeek) */
const getNextRunFromCron = (cronExpression: string): Date => {
  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length < 5) return new Date();
  const minute = parseInt(parts[0], 10) || 0;
  const hour = parseInt(parts[1], 10) || 0;
  const dayOfMonth = parts[2];
  const dayOfWeek = parts[4];
  const now = new Date();
  const next = new Date(now);
  next.setSeconds(0, 0);

  if (dayOfMonth !== '*' && dayOfWeek === '*') {
    const dom = parseInt(dayOfMonth, 10) || 1;
    next.setUTCDate(dom);
    next.setUTCHours(hour, minute, 0, 0);
    if (next.getTime() <= now.getTime()) next.setUTCMonth(next.getUTCMonth() + 1);
    return next;
  }
  if (dayOfMonth === '*' && dayOfWeek !== '*') {
    const dow = parseInt(dayOfWeek, 10) ?? 0;
    const currDow = now.getUTCDay();
    const daysToAdd = (dow - currDow + 7) % 7;
    next.setUTCDate(next.getUTCDate() + daysToAdd);
    next.setUTCHours(hour, minute, 0, 0);
    if (next.getTime() <= now.getTime()) next.setUTCDate(next.getUTCDate() + 7);
    return next;
  }
  next.setUTCHours(hour, minute, 0, 0);
  if (next.getTime() <= now.getTime()) next.setUTCDate(next.getUTCDate() + 1);
  return next;
};

/** Calcula la próxima fecha desde startDate + periodicity (fallback cuando no hay cron) */
const calculateNextDeliveryFromPeriodicity = (
  startDate: Date,
  periodicity: string
): Date => {
  const next = new Date(startDate);
  const now = new Date();
  const period = periodicity.toLowerCase();
  while (next.getTime() <= now.getTime()) {
    if (period.includes('week')) next.setDate(next.getDate() + 7);
    else if (period.includes('month')) next.setMonth(next.getMonth() + 1);
    else next.setDate(next.getDate() + 7);
  }
  return next;
};

/** Extrae array de la respuesta del API (array directo o { data/items/list/result }) */
export const extractListFromResponse = (data: unknown): unknown[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const arr = obj.data ?? obj.items ?? obj.list ?? obj.subscriptions ?? obj.value ?? obj.result;
    return Array.isArray(arr) ? arr : [];
  }
  return [];
};

/** Mapea item del API a EdgeSubscriptionData */
export const mapApiItemToEdgeSubscription = (item: Record<string, unknown>): EdgeSubscriptionData => {
  const get = (...keys: string[]) => {
    for (const k of keys) {
      const v = item[k];
      if (v != null && typeof v === 'string') return v;
    }
    return '';
  };
  const getBool = (...keys: string[]): boolean | null => {
    for (const k of keys) {
      const v = item[k];
      if (typeof v === 'boolean') return v;
    }
    return null;
  };
  const getDate = (...keys: string[]) => {
    for (const k of keys) {
      const v = item[k];
      if (!v) continue;
      if (v instanceof Date) return v;
      let raw = String(v);
      // Si no tiene offset explícito (Z, +HH:mm, -HH:mm), asumir UTC añadiendo Z
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw) && !/[Zz]$/.test(raw) && !/[+-]\d{2}:?\d{2}$/.test(raw)) {
        raw = raw + 'Z';
      }
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return new Date();
  };
  const id = String(item.edgeGuid ?? item.id ?? item.guid ?? item.subscriptionId ?? '');
  const recipientsArr = item.recipients;
  const recipientStr = Array.isArray(recipientsArr)
    ? recipientsArr.filter((x): x is string => typeof x === 'string').join(', ')
    : get('recipient', 'recipientEmail', 'recipient_email');
  const edgeTypeRaw = (get('edgeType', 'edge_type') || 'brand').toLowerCase();
  const edgeType = (['brand', 'opportunity', 'inventory'].includes(edgeTypeRaw)
    ? edgeTypeRaw
    : 'brand') as 'brand' | 'opportunity' | 'inventory';
  const rawPeriodicity = get('edgePeriodicity', 'periodicity', 'frequency') || 'Weekly';
  const periodicity = formatPeriodicityFromApi(rawPeriodicity);
  const createdOn = getDate('createdUtc', 'createdOn', 'created_on');
  const modifiedOn = item.modifiedUtc != null && String(item.modifiedUtc) !== ''
    ? getDate('modifiedUtc', 'modifiedOn', 'modified_on')
    : createdOn;
  const stateVal = get('state', 'state_code') ?? '';
  const territoryGuidVal = get('territoryGuid', 'territory_guid') ?? '';
  const territoryVal = get('territoryName', 'territory_name') || territoryGuidVal || '';
  const startDate = getDate('startDate', 'start_date') || createdOn;
  const cronExpression = get('cronExpression', 'cron_expression', 'cron')?.trim() ?? '';
  const isOneTime = !cronExpression;
  const sendOnce = getBool('sendOnce', 'send_once') ?? isOneTime;
  const apiNext = item.nextDelivery ?? item.next_delivery ?? item.nextRun ?? item.next_run;
  const apiNextDate = apiNext != null && String(apiNext) !== ''
    ? getDate('nextDelivery', 'next_delivery', 'nextRun', 'next_run')
    : null;
  const createdMs = createdOn.getTime();
  const isSameAsCreated = apiNextDate && Math.abs(apiNextDate.getTime() - createdMs) < 60000;
  let nextDelivery: Date;
  if (isOneTime) {
    nextDelivery = startDate;
  } else if (apiNextDate && !isSameAsCreated) {
    nextDelivery = apiNextDate;
  } else if (cronExpression) {
    nextDelivery = getNextRunFromCron(cronExpression);
  } else {
    nextDelivery = calculateNextDeliveryFromPeriodicity(startDate, periodicity);
  }
  return {
    id,
    subscriptionName: get('subscriptionName', 'subscription_name'),
    edgeType,
    recipient: recipientStr,
    recipientEmail: recipientStr,
    state: stateVal,
    territory: territoryVal || undefined,
    brandName: get('brandName', 'brand_name'),
    periodicity,
    category: get('category') || undefined,
    startDate,
    nextDelivery,
    cronExpression: cronExpression || undefined,
    sendOnce,
    isActive: getBool('isActive', 'is_active') ?? false,
    createdOn,
    modifiedOn,
    company: get('company', 'company_name') || undefined,
    externalMessage: get('externalMessage', 'external_message') || undefined,
  };
};
